import React from "react";
import { css, useTheme } from "@emotion/react";

import useBreakpoint from "core/hooks/useBreakpoint";
import useWindowSize from "core/hooks/useWindowSize";
import VideoPlayer from "core/Components/Video/ReactVideoPlayer/VideoPlayer";
import {
    CATALOGS_WIDTH,
    MD_MAX_WIDTH,
    VIDEO_RATIOS
} from "elements/Components/SegmentedVideo/includes/consts";
import RightSection from "elements/Components/SegmentedVideo/components/RightSection";
import VideoCatalogContainer from "elements/Components/SegmentedVideo/components/VideoCatalogs";

type Props = {
    videoItems?: SegmentedVideoItem[],
    currentVideoItem: SegmentedVideoItem,
    themeColour: string,
    hasNextVideo: boolean,
    onVideoStart: () => void,
    onNextVideo: () => void,
    onChangeCurrentTopic: (urlSegment: string) => void,
};

const SegmentedVideoItem: React.FC<Props> = ({
    videoItems = [],
    currentVideoItem,
    themeColour,
    hasNextVideo,
    onVideoStart,
    onNextVideo,
    onChangeCurrentTopic,
}) => {
    const theme = useTheme();
    const { maxWidth } = useBreakpoint();
    const { width: windowWidth } = useWindowSize();

    const watchedVideoItems = videoItems?.filter(
        (videoItem: SegmentedVideoItem) => (videoItem.isWatched)
    );
    const totalVideoCount = videoItems.length;
    const watchedVideoCount = watchedVideoItems.length;
    const watchedVideoPercent = ((watchedVideoCount / totalVideoCount) * 100) || 0;

    const style = css`
        display: flex;
        flex-direction: row;
        width: 100%;
        height: 100%;
    `;

    const rightSectionContainerStyle = css`
        ${videoItems.length > 1 ? css`
            width: calc(100% - ${CATALOGS_WIDTH}px);
        ` : css`
            width: 100%;
        `}
        
        ${theme.breakpoints.down("md")} {
            width: 100%;
        }
    `;

    const rightSectionStyle = css`
        position: relative;
        margin: 0 auto;
        height: 100%;

        .video-player-container {
            ${theme.breakpoints.up("4xl")} {
                max-height: 720px;
                min-height: 720px;
            }

            ${theme.breakpoints.between("xxl", "3xl")} {
                max-height: 512px;
                min-height: 512px;
            }

            ${theme.breakpoints.only("xl")} {
                max-height: 456px;
                min-height: 456px;
            }

            ${theme.breakpoints.only("lg")} {
                max-height: 371px;
                min-height: 371px;
            }

            ${theme.breakpoints.only("md")} {
                max-height: 432px;
                min-height: 432px;
            }

            ${theme.breakpoints.down("sm")} {
                height: ${windowWidth * VIDEO_RATIOS}px;
                min-height: ${windowWidth * VIDEO_RATIOS}px;
            }
        }
    `;

    const videoPlayerContainer = css`
        position: relative;
        width: 100%;
        height: 100%;
        margin: 0;
        padding: 0;
        border-top-left-radius: inherit;
        border-top-right-radius: inherit;
        overflow: hidden;
    `;

    const buttonContainerStyle = css`
        display: flex;
        justify-content: end;
        margin-top: 10px;
        width: 100%;
    `;

    const buttonStyle = css`
        min-width: 80px;
        height: 32px;
        font-size: 16px;
        color: ${themeColour};
        background-color: white;
        border: 1px solid ${themeColour};
        border-radius: 6px;
        transition: background-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
        cursor: pointer;
        
        &:focus {
            outline: none;
        }
        
        &:hover {
            color: white;
            background-color: ${themeColour};
        }
    `;

    return (
        <div css={style}>
            {(maxWidth > MD_MAX_WIDTH && videoItems.length > 1) && (
                <VideoCatalogContainer
                    videoItems={videoItems}
                    currentVideoItem={currentVideoItem}
                    colour={themeColour}
                    totalVideoCount={totalVideoCount}
                    watchedVideoCount={watchedVideoCount}
                    watchedVideoPercent={watchedVideoPercent}
                    onChangeCurrentTopic={onChangeCurrentTopic}
                />
            )}
            <div css={rightSectionContainerStyle}>
                <RightSection css={rightSectionStyle}>
                    <div css={videoPlayerContainer} className={"video-player-container"}>
                        <VideoPlayer
                            playlistUrl={currentVideoItem.videoURL}
                            videos={[]}
                            onStart={onVideoStart}
                        />
                    </div>
                    {hasNextVideo && (
                        <div css={buttonContainerStyle}>
                            <button css={buttonStyle} onClick={onNextVideo}>Next</button>
                        </div>
                    )}
                </RightSection>
            </div>
        </div>
    );
};

export default SegmentedVideoItem;